import {
  useEventByIdQuery,
  useEventSessionAvailabilityQuery,
} from "gql/generated";
import {
  buildEventSessionOptions,
  getEventPurchaseFlow,
  getResolvedProductSellingConfig,
} from "shared/utils";
import { useMemo } from "react";
import { useBookerContext } from "widgets/booker/contexts";

export const useEventPurchaseFlow = () => {
  const { product } = useBookerContext();
  const eventId = product?.eventMetadata?.event ?? undefined;
  const granularity = product?.eventMetadata?.sessionGranularity;

  const { data: eventData, loading: eventLoading } = useEventByIdQuery({
    variables: { id: eventId ?? "" },
    skip: !eventId,
  });

  // A widget may restrict which sessions it offers (empty = all).
  const includedSessionIds = product?.eventMetadata?.includedSessionIds ?? [];

  const sessionOptions = useMemo(() => {
    const built = buildEventSessionOptions(
      eventData?.eventById as any,
      granularity,
    );
    const included = (includedSessionIds ?? []).filter(Boolean);
    if (included.length === 0) return built;
    const allowed = new Set(included);
    return built.filter(session => allowed.has(session.sessionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData?.eventById, granularity, includedSessionIds.join(",")]);

  const { data: availabilityData, loading: availabilityLoading } =
    useEventSessionAvailabilityQuery({
      variables: {
        productId: product?._id ?? "",
        sessionIds: sessionOptions.map(session => session.sessionId),
      },
      skip: !product?._id || sessionOptions.length === 0,
    });

  const availabilityBySessionId = useMemo(
    () =>
      Object.fromEntries(
        (availabilityData?.eventSessionAvailability ?? [])
          .filter(Boolean)
          .map(item => [item!.sessionId, item!]),
      ),
    [availabilityData?.eventSessionAvailability],
  );

  // Only today and future sessions are bookable.
  const todayISO = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);
  const bookableSessions = useMemo(
    () => sessionOptions.filter(session => session.dateISO >= todayISO),
    [sessionOptions, todayISO],
  );

  // Sessions grouped by day, with the distinct day list (sorted).
  const sessionsByDate = useMemo(() => {
    const grouped: Record<string, typeof bookableSessions> = {};
    for (const session of bookableSessions) {
      (grouped[session.dateISO] ??= []).push(session);
    }
    return grouped;
  }, [bookableSessions]);

  const days = useMemo(
    () => Object.keys(sessionsByDate).sort(),
    [sessionsByDate],
  );

  const hasTimeSlots = useMemo(
    () => bookableSessions.some(session => Boolean(session.timeLabel)),
    [bookableSessions],
  );

  /** A day is sold out when every one of its sessions is sold out. */
  const isDaySoldOut = (dateISO: string): boolean => {
    const sessions = sessionsByDate[dateISO] ?? [];
    if (sessions.length === 0) return false;
    return sessions.every(session => {
      const availability = availabilityBySessionId[session.sessionId];
      return (
        !availability?.isUnlimited &&
        (availability?.remainingCapacity ?? 0) <= 0
      );
    });
  };

  const tomorrowISO = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const isToday = (dateISO: string) => dateISO === todayISO;
  const isTomorrow = (dateISO: string) => dateISO === tomorrowISO;

  const hasNoFutureSessions =
    !eventLoading && sessionOptions.length > 0 && bookableSessions.length === 0;

  return {
    event: eventData?.eventById,
    eventLoading,
    availabilityLoading,
    sessionOptions,
    bookableSessions,
    sessionsByDate,
    days,
    dayCount: days.length,
    hasTimeSlots,
    isDaySoldOut,
    isToday,
    isTomorrow,
    hasNoFutureSessions,
    availabilityBySessionId,
    // Past sessions aren't choices, so one remaining session needs no picker.
    // With none remaining, keep the picker so it can show the no-dates notice.
    flow: getEventPurchaseFlow(
      product as any,
      bookableSessions.length > 0
        ? bookableSessions.length
        : sessionOptions.length,
    ),
    sellingConfig: getResolvedProductSellingConfig(
      product as typeof product & { sellingConfig?: any },
    ),
  };
};

/** Cards in place below this many distinct days; calendar picker at or above. */
export const DAY_CARD_THRESHOLD = 10;
